import { ApiError } from 'core/errors/ApiError';

export function mapFieldErrors(errors = {}) {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return {};
  }

  return Object.entries(errors).reduce((mapped, [path, messages]) => {
    const list = Array.isArray(messages) ? messages.filter(Boolean) : [messages].filter(Boolean);
    if (list.length > 0) {
      mapped[path] = list.map(String);
    }
    return mapped;
  }, {});
}

export function firstFieldError(errors = {}, path) {
  const mapped = mapFieldErrors(errors);
  return mapped[path]?.[0] || null;
}

export function flattenFieldErrors(errors = {}) {
  return Object.entries(mapFieldErrors(errors)).flatMap(([path, messages]) =>
    messages.map((message) => ({ path, message })),
  );
}

export function getNestedError(errors = {}, path) {
  return firstFieldError(errors, path);
}

export function mapIndexedWeightErrors(errors = {}, submitted = []) {
  const mapped = mapFieldErrors(errors);
  submitted.forEach((row, index) => {
    const from = `weights.${index}.weight`;
    const answerId = row?.assessment_answer_id;
    if (mapped[from] && answerId != null) {
      mapped[`weights.${answerId}.weight`] = mapped[from];
    }
  });
  return mapped;
}

export function humanizeApiError(error, t) {
  if (!(error instanceof ApiError)) {
    return t('errors.unexpected');
  }
  if (error.isNetworkError) {
    return t('errors.network');
  }
  if (error.isThrottled()) {
    return t('errors.throttled');
  }
  if (error.isGenerationInProgress()) {
    return t('results.generationLock');
  }
  if (error.isStaleRevision()) {
    return t('errors.staleRevision');
  }
  if (error.isPayloadTooLarge()) {
    return t('workbook.errors.fileTooLarge');
  }
  if (error.isUnsupportedMedia()) {
    return t('workbook.errors.unsupportedUploadMediaType');
  }
  if (error.isGone()) {
    return t('workbook.newQuestions.errors.expiredPreview');
  }
  if (error.isBadRequest()) {
    return t('workbook.errors.malformed');
  }
  if (error.isConflict()) {
    return error.message || t('workbook.errors.staleRevision');
  }
  if (error.isValidation()) {
    return error.message || t('errors.validation');
  }
  if (error.isForbidden()) {
    return t('errors.forbidden');
  }
  if (error.isNotFound()) {
    return t('errors.notFound');
  }
  if (error.status >= 500) {
    const message = String(error.message || '').trim();
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(message);
    const looksLikeTrace = /stack trace|exception|sqlstate|traceback/i.test(message);
    if (message && !looksLikeHtml && !looksLikeTrace && message.length <= 240) {
      return message;
    }
    return t('errors.server');
  }
  return error.message || t('errors.unexpected');
}
